import { pushErrorMetric } from "@/components/core/ConnectCCP/error-metrics"
import { SentryLogger } from "@/helpers/sentryLogger"
import { isCallErrorKind } from "@/models/call-errors"

import { checkResetSession } from "./connect_session"
import { getSnapshot } from "./contact_helpers"
import {
  RefreshConnectSessionInputType,
  SetAgent,
  SoftphoneErrorHandlerInputType,
} from "./types"

const log = new SentryLogger()

const checkAndRefreshConnectSession = async (
  refreshConnectSession: RefreshConnectSessionInputType["refreshConnectSession"],
): Promise<void> => {
  const shouldResetSession = checkResetSession()

  // Re-authenticate with AWS connect; The AWS Connect refresh token can only be re-used once
  if (shouldResetSession) {
    await refreshConnectSession()
  }
}

const handleAgentRefresh = (setAgent: SetAgent) => (agent: connect.Agent) => {
  try {
    setAgent(agent)
  } catch (error) {
    log.error(error, { agent: getSnapshot(agent) })
  }
}

/** Event handler for the agent status change.
 * @param refreshConnectSession - function to refresh the AWS Connect session
 * @returns void
 *
 * Attempts to refresh the Amazon Connect token when agent goes from Offline to Available to prevent the agent from being disconnected after 10 hours of being online.
 * We didn't attempt to do this while the agent is online because we're not sure whether it might disconnect the agent while in a call;
 * TODO: Needs more testing and research to make the token refresh in all kinds of agent states.
 */
const handleAgentStateChange =
  ({ refreshConnectSession }: RefreshConnectSessionInputType) =>
  async ({
    agent,
    newState,
    oldState,
  }: connect.AgentStateChange): Promise<void> => {
    try {
      if (oldState === "Offline" && newState === "Available") {
        await checkAndRefreshConnectSession(refreshConnectSession)
      }
    } catch (error) {
      log.error(error, { agent: getSnapshot(agent) })
    }
  }

/**
 * Normalise errorType strings that cross the CCP iframe boundary in title case
 * (e.g. "Ice Collection Timeout") back to the snake_case form used by
 * connect.SoftphoneErrorTypes (e.g. "ice_collection_timeout").
 */
const normalizeErrorType = (errorType: string): string =>
  errorType.replace(/\s+/g, "_").toLowerCase()

const isConnectSoftphoneError = (
  error: unknown,
): error is connect.SoftphoneError => {
  if (error instanceof connect.SoftphoneError) {
    return Boolean(error.errorType)
  }

  if (typeof error === "object" && error !== null && "errorType" in error) {
    return Boolean(error.errorType)
  }

  return false
}
const logUnhandledSoftphoneError = async (
  softPhoneError: connect.SoftphoneError,
  agentRef: SoftphoneErrorHandlerInputType["agentRef"],
) => {
  const errorType = normalizeErrorType(softPhoneError.errorType)
  switch (errorType) {
    case connect.SoftphoneErrorTypes.SIGNALLING_HANDSHAKE_FAILURE:
      await pushErrorMetric(
        agentRef,
        "callpanel-network-error-handshake-failure",
      )
      break
    case connect.SoftphoneErrorTypes.SIGNALLING_CONNECTION_FAILURE:
      await pushErrorMetric(
        agentRef,
        "callpanel-network-error-signalling-connection",
      )
      break
    case connect.SoftphoneErrorTypes.WEBRTC_ERROR:
      await pushErrorMetric(agentRef, "callpanel-network-error-webrtc")
      break
    case connect.SoftphoneErrorTypes.ICE_COLLECTION_TIMEOUT:
      await pushErrorMetric(
        agentRef,
        "callpanel-network-error-ice-collection-timeout",
      )
      break
    case connect.SoftphoneErrorTypes.REALTIME_COMMUNICATION_ERROR:
      await pushErrorMetric(agentRef, "callpanel-network-error-webrtc")
      break
    case connect.SoftphoneErrorTypes.OTHER:
    case connect.SoftphoneErrorTypes.USER_BUSY_ERROR:
      break
    default:
      log.error(
        `Unexpected softphone error: ${errorType} - ${softPhoneError.errorMessage ?? ""}`,
        {
          agent: getSnapshot(agentRef.current),
          isSoftphoneError: true,
        },
      )
      break
  }
}

/**
 * Event handler triggered whenever the iframe throws a softphone error. We've first encountered this when
 * agent's settings in the browser have microphone access disabled. See https://app.clickup.com/t/7508642/POC-4339
 *
 */
const handleSoftphoneError =
  ({
    agentRef,
    pushNetworkingError,
    showBrowserErrorModal,
    showMicrophoneErrorModal,
  }: SoftphoneErrorHandlerInputType) =>
  async (initialError: connect.SoftphoneError): Promise<void> => {
    try {
      // Connect types are wrong again. the typing errorType: string can be also undefined
      // RtcPeerConnectionManagerV2 Shared media session failed undefined was caught as
      // Unexpected softphone error: undefined - undefined,
      // Just log the error in the catch block.
      if (!isConnectSoftphoneError(initialError)) {
        throw new Error("Unexpected softphone error", { cause: initialError })
      }

      const errorType = normalizeErrorType(initialError.errorType)

      if (isCallErrorKind(errorType)) {
        pushNetworkingError(errorType)
      }

      const isMicError =
        errorType === connect.SoftphoneErrorTypes.MICROPHONE_NOT_SHARED ||
        errorType === "gum_timeout_failure"

      const isUnsupportedBrowser =
        errorType === connect.SoftphoneErrorTypes.UNSUPPORTED_BROWSER

      if (isMicError) {
        await showMicrophoneErrorModal()
      } else if (isUnsupportedBrowser) {
        await showBrowserErrorModal()
      } else {
        // fire and forget. Async function `logUnhandledSoftphoneError` intentionally not awaited
        // because it calls `pushErrorMetric` which is a fire and forget function
        logUnhandledSoftphoneError(initialError, agentRef)
      }
    } catch (error) {
      log.error(error, {
        agent: getSnapshot(agentRef.current),
        isSoftphoneError: true,
      })
    }
  }

export {
  handleAgentRefresh,
  handleAgentStateChange,
  handleSoftphoneError,
  normalizeErrorType,
}
